"use client";

import {
  addKeyGrantFn,
  getKeyGrantFn,
  removeKeyGrantFn,
} from "~/lib/functions/encrypted-data";
import type { KeyGrantResourceType } from "~/lib/schemas/key-grants";

import {
  base64UrlToBuffer,
  unwrapDek,
  wrapDek,
  type WrappedDek,
} from "./crypto";
import type { KeyManager } from "./key-manager";

export interface ShareTarget {
  userId: string;
  publicKey: string;
}

/**
 * Re-wrap a key the caller holds for another person, without touching the
 * record it opens: unwrap the caller's own wrapped key with their private key
 * and wrap the key for the target's public key.
 */
export async function rewrapDek(
  ownWrappedDekJson: string,
  privateKey: CryptoKey,
  targetPublicKey: string,
): Promise<string> {
  const wrappedDek: WrappedDek = JSON.parse(ownWrappedDekJson);
  const ephemeralPubKey = await crypto.subtle.importKey(
    "raw",
    base64UrlToBuffer(wrappedDek.ephemeralPublicKey),
    { name: "ECDH", namedCurve: "P-256" },
    true,
    [],
  );
  const dek = await unwrapDek(wrappedDek, ephemeralPubKey, privateKey);
  const publicKey = await crypto.subtle.importKey(
    "raw",
    base64UrlToBuffer(targetPublicKey),
    { name: "ECDH", namedCurve: "P-256" },
    true,
    [],
  );
  return JSON.stringify(await wrapDek(dek, publicKey));
}

/**
 * Grant another user access to an already-encrypted resource without
 * re-encrypting: unwraps the owner's DEK and re-wraps it for the target.
 * Used when toggling an existing note from private to shared.
 */
export async function grantResourceAccess(
  keyManager: KeyManager,
  resourceType: KeyGrantResourceType,
  resourceId: string,
  target: ShareTarget,
): Promise<void> {
  const ownGrantJson = await getKeyGrantFn({
    data: { resourceType, resourceId },
  });
  if (!ownGrantJson) {
    throw new Error("No key grant found for resource — cannot share");
  }

  await addKeyGrantFn({
    data: {
      resourceType,
      resourceId,
      userId: target.userId,
      encryptedDek: await rewrapDek(
        ownGrantJson,
        keyManager.getPrivateKey(),
        target.publicKey,
      ),
    },
  });
}

/**
 * Revoke another user's access to a resource (toggling shared → private).
 * Best-effort: the ciphertext is no longer served to them either way.
 */
export async function revokeResourceAccess(
  resourceType: KeyGrantResourceType,
  resourceId: string,
  userId: string,
): Promise<void> {
  await removeKeyGrantFn({ data: { resourceType, resourceId, userId } });
}
